import { z } from "zod";
import { PhysicianSummarySchema } from "./patient-physician.js";
import { IdSchema } from "./primitives.js";
/** Site room inventory. A zone labels the room and never assigns its patient to a clinician. */
export const ClinicRoomSchema = z
    .object({
    id: IdSchema,
    siteId: IdSchema,
    name: z.string().min(1).max(40),
    zone: z.string().max(80),
    zonePhysician: PhysicianSummarySchema.optional(),
    rosterPhysician: PhysicianSummarySchema.optional(),
    state: z.enum(["available", "occupied", "turnover"]),
    version: z.number().int().positive(),
    appointmentId: IdSchema.optional(),
    appointmentVersion: z.number().int().positive().optional(),
    appointmentStatus: z.enum(["roomed", "in_progress", "ready_for_discharge"]).optional(),
    encounterId: IdSchema.optional(),
    encounterStatus: z
        .enum(["open", "ready_for_review", "signed", "amended", "cancelled"])
        .optional(),
    encounterSigned: z.boolean().optional(),
    patientId: IdSchema.optional(),
    displayName: z.string().optional(),
    clinician: z.string().optional(),
})
    .passthrough();
export const ClinicRoomsSchema = z.array(ClinicRoomSchema);
export const ClinicRoomsQuerySchema = z.object({ siteId: IdSchema }).strict();
const RoomNameSchema = z
    .string()
    .trim()
    .min(1)
    .max(40)
    .refine((name) => !["lobby", "unassigned"].includes(name.toLowerCase()), "Choose a room name other than Lobby or Unassigned");
export const CreateClinicRoomRequestSchema = z
    .object({
    siteId: IdSchema,
    name: RoomNameSchema,
    zone: z.string().trim().max(80),
    zonePhysicianUserId: IdSchema.nullable().optional(),
    idempotencyKey: IdSchema,
})
    .strict();
export const UpdateClinicRoomRequestSchema = z
    .object({
    siteId: IdSchema,
    expectedVersion: z.number().int().positive(),
    /** A rename also relabels the room's current visit; finished visits keep the old name. */
    name: RoomNameSchema.optional(),
    zone: z.string().trim().max(80).optional(),
    zonePhysicianUserId: IdSchema.nullable().optional(),
    targetState: z.enum(["available", "turnover"]).optional(),
})
    .strict()
    .refine((input) => input.name !== undefined ||
    input.zone !== undefined ||
    input.targetState !== undefined ||
    input.zonePhysicianUserId !== undefined, "Provide a name, zone, physician, or room state to update");
/** Only an empty room can be deleted; the version guards against a stale board. */
export const DeleteClinicRoomRequestSchema = z
    .object({
    siteId: IdSchema,
    expectedVersion: z.number().int().positive(),
})
    .strict();
export const DeletedClinicRoomSchema = z
    .object({
    id: IdSchema,
    siteId: IdSchema,
    name: z.string().min(1).max(40),
    deleted: z.literal(true),
})
    .strict();
export const AssignAppointmentRoomRequestSchema = z
    .object({
    siteId: IdSchema,
    expectedVersion: z.number().int().positive(),
    roomId: IdSchema,
    expectedRoomVersion: z.number().int().positive(),
})
    .strict();
/** Releasing a room compares the current appointment and occupied-room versions. */
export const ReleaseAppointmentRoomRequestSchema = AssignAppointmentRoomRequestSchema;
//# sourceMappingURL=clinic-rooms.js.map